import React, { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import type { DragSourceMonitor } from 'react-dnd';

interface Item {
  id: string;
  type: 'image' | 'text' | 'sticky';
  content: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  color?: string;
  fontSize?: number;
  fontFamily?: string;
  layer: number;
  author?: string;
  timestamp?: string;
}

interface Props {
  item: Item;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onUpdate: (id: string, updates: Partial<Item>) => void;
  onLayerChange: (id: string, direction: 'up' | 'down') => void;
}

const DraggableItem: React.FC<Props> = ({
  item,
  isSelected,
  onSelect,
  onUpdate,
  onLayerChange,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0 });
  const [currentSize, setCurrentSize] = useState(item.size);
  const [showControls, setShowControls] = useState(false);

  const [{ isDragging }, drag] = useDrag({
    type: 'ITEM',
    item: { ...item },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'ITEM',
    hover: (draggedItem: Item) => {
      if (draggedItem.id !== item.id) {
        // Handle hover logic if needed
      }
    },
  });

  drag(drop(ref));

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({ x: e.clientX, y: e.clientY });
  };

  const handleResizeMove = (e: React.MouseEvent) => {
    if (isResizing) {
      const dx = e.clientX - resizeStart.x;
      const dy = e.clientY - resizeStart.y;
      
      setCurrentSize(prev => ({
        width: Math.max(50, prev.width + dx),
        height: Math.max(50, prev.height + dy),
      }));
      
      setResizeStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
    onUpdate(item.id, { size: currentSize });
  };

  React.useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove as any);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove as any);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing]);

  const handleTextChange = (e: React.FormEvent<HTMLDivElement>) => {
    onUpdate(item.id, { content: e.currentTarget.textContent || '' });
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate(item.id, { color: e.target.value });
  };

  const handleFontSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate(item.id, { fontSize: parseInt(e.target.value) });
  };

  const handleFontFamilyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate(item.id, { fontFamily: e.target.value });
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      onUpdate(item.id, { content: '' });
    }
  };

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, handleResizeEnd, handleResizeMove]);

  const renderContent = () => {
    switch (item.type) {
      case 'image':
  return (
          <img
            src={item.content}
            alt="Mood board item"
            className="w-full h-full object-cover"
          />
        );
      case 'sticky':
        return (
          <div
            contentEditable
            suppressContentEditableWarning
            onInput={handleTextChange}
            style={{
              color: '#000000',
              fontSize: item.fontSize,
              fontFamily: item.fontFamily,
              backgroundColor: item.color,
            }}
            className="w-full h-full p-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-md"
          >
            {item.content}
          </div>
        );
      default: // text
        return (
          <div
            contentEditable
            suppressContentEditableWarning
            onInput={handleTextChange}
            style={{
              color: item.color,
              fontSize: item.fontSize,
              fontFamily: item.fontFamily,
            }}
            className="w-full h-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {item.content}
          </div>
        );
    }
  };

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        left: item.position.x,
        top: item.position.y,
        width: currentSize.width,
        height: currentSize.height,
        opacity: isDragging ? 0.5 : 1,
        cursor: isResizing ? 'nwse-resize' : 'move',
        zIndex: item.layer,
      }}
      className="group"
      onClick={() => onSelect(item.id)}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      <div 
        className={`w-full h-full bg-white rounded shadow-lg overflow-hidden ${
          isSelected ? 'ring-2 ring-blue-500' : ''
        }`}
      >
        {renderContent()}
      </div>

      {showControls && (
        <div className="absolute top-0 right-0 transform -translate-y-full bg-white rounded shadow-lg p-2 flex space-x-2">
          {(item.type === 'text' || item.type === 'sticky') && (
            <>
              <input
                type="color"
                value={item.color}
                onChange={handleColorChange}
                className="w-8 h-8 rounded cursor-pointer"
              />
              <select
                value={item.fontSize}
                onChange={handleFontSizeChange}
                className="px-2 py-1 border rounded text-xs"
              >
                <option value="12">12px</option>
                <option value="14">14px</option>
                <option value="16">16px</option>
                <option value="20">20px</option>
                <option value="24">24px</option>
                <option value="32">32px</option>
              </select>
              <select
                value={item.fontFamily}
                onChange={handleFontFamilyChange}
                className="px-2 py-1 border rounded text-xs"
              >
                <option value="Inter">Inter</option>
                <option value="Arial">Arial</option>
                <option value="Helvetica">Helvetica</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Georgia">Georgia</option>
              </select>
            </>
          )}
          
          <button
            onClick={() => onLayerChange(item.id, 'up')}
            className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
          >
            ↑
          </button>
          <button
            onClick={() => onLayerChange(item.id, 'down')}
            className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
          >
            ↓
          </button>
        </div>
      )}
    </div>
  );
};

export default DraggableItem; 